// Project Overview → Activity (Stephanie 2026-10-13/14): plain sentences that
// name the document and link to it, "Add a note", delete your own notes
// (admins any), All / Notes / Updates, and Show more.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { timeAgo } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { MentionTextarea, NoteText } from "@/components/notes/MentionTextarea";
import { EditDeleteNote } from "@/components/notes/EditDeleteNote";

type Filter = "all" | "notes" | "updates";

interface ActivityItem {
  id: string;
  kind: "note" | "update";
  sentence: string;
  docLabel: string | null;
  href: string | null;
  body: string | null;
  actorUserId: string | null;
  actorName: string | null;
  occurredAt: string;
}

const PAGE = 20;
const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: "all", label: "All" },
  { key: "notes", label: "Notes" },
  { key: "updates", label: "Updates" },
];

/** The sentence with the document's name as a link. */
function Sentence({ item }: { item: ActivityItem }) {
  if (!item.docLabel || !item.href) return <>{item.sentence}</>;
  const at = item.sentence.indexOf(item.docLabel);
  if (at < 0) return <>{item.sentence}</>;
  return (
    <>
      {item.sentence.slice(0, at)}
      <Link to={item.href} className="font-semibold text-ink underline-offset-2 hover:underline">
        {item.docLabel}
      </Link>
      {item.sentence.slice(at + item.docLabel.length)}
    </>
  );
}

export function ProjectActivityFeed({ projectId }: { projectId: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");
  const [limit, setLimit] = useState(PAGE);
  const [draft, setDraft] = useState("");
  // "@" tags (Stephanie 2026-10-21): they get the bell + an email.
  const [mentions, setMentions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const key = ["project", projectId, "activity", filter, limit] as const;
  const feed = useQuery({
    queryKey: key,
    queryFn: () =>
      api<{ items: ActivityItem[]; hasMore: boolean }>(
        `/projects/${projectId}/activity?filter=${filter}&limit=${limit}`,
      ),
    placeholderData: (prev) => prev,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["project", projectId, "activity"] });

  const addNote = useMutation({
    mutationFn: (body: string) =>
      api(`/projects/${projectId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body, mention_user_ids: mentions }),
      }),
    onSuccess: () => {
      setDraft("");
      setMentions([]);
      setError(null);
      void refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? ((e.body as { error?: string } | null)?.error ?? "Couldn't save the note.") : "Couldn't save the note."),
  });


  const items = feed.data?.items ?? [];

  return (
    <div className="rounded-md border border-rule bg-white p-5 sm:p-[22px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Activity</h2>
        <div className="inline-flex rounded-md border border-rule p-0.5" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              aria-pressed={filter === f.key}
              onClick={() => {
                setFilter(f.key);
                setLimit(PAGE);
              }}
              className={`rounded px-3 py-1 text-xs font-semibold ${filter === f.key ? "bg-ink text-white" : "text-muted hover:text-ink"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Add a note */}
      <form
        className="mt-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) addNote.mutate(draft.trim());
        }}
      >
        <label htmlFor="project-note" className="sr-only">
          Add a note
        </label>
        <MentionTextarea
          aria-label="Add a note"
          rows={2}
          value={draft}
          onChange={(t, ids) => { setDraft(t); setMentions(ids); }}
          maxLength={5000}
          placeholder="Add a note for the team… type @ to tag someone"
          className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-ink focus:outline-none"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          {error ? <p role="alert" className="text-xs text-risk">{error}</p> : <span />}
          <Button type="submit" size="sm" disabled={!draft.trim() || addNote.isPending}>
            {addNote.isPending ? "Saving…" : "Add note"}
          </Button>
        </div>
      </form>

      {feed.isLoading ? (
        <div className="mt-4 h-16 animate-pulse rounded bg-cream-50" aria-label="Loading activity" />
      ) : feed.isError ? (
        <p role="alert" className="mt-4 text-sm text-risk">We couldn't load the activity. Refresh to try again.</p>
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-muted">
          {filter === "notes"
            ? "No notes yet."
            : "No activity yet. Presentations, quotes, POs and invoices will show here as they happen."}
        </p>
      ) : (
        <ol className="mt-4">
          {items.map((it) => {
            // Anyone can edit or delete a note (Stephanie 2026-10-30) — was
            // the author or an admin only.
            const isNote = it.kind === "note";
            return (
              <li key={it.id} className="flex gap-3 border-b border-[#e7e7e4] py-3 last:border-0 last:pb-0">
                <div
                  className={`mt-[7px] flex h-1.5 w-1.5 flex-shrink-0 rounded-full ${it.kind === "note" ? "bg-rust-500" : "bg-ink"}`}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="text-sm text-ink">
                      <Sentence item={it} />
                    </span>
                    <span className="flex items-center gap-2 text-xs text-muted">
                      {timeAgo(it.occurredAt)}
                    </span>
                  </div>
                  {isNote ? (
                    <EditDeleteNote
                      fields={[{ key: "body", label: "Note", value: it.body ?? "" }]}
                      onSave={async (v) => {
                        await api(`/projects/${projectId}/notes/${it.id}`, {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ body: (v.body ?? "").trim() }),
                        });
                        await refresh();
                      }}
                      onDelete={async () => {
                        await api(`/projects/${projectId}/notes/${it.id}`, { method: "DELETE" });
                        await refresh();
                      }}
                    >
                      {it.body && (
                        <p className="mt-1.5 whitespace-pre-wrap rounded-md bg-cream-50 px-3 py-2 text-sm text-ink"><NoteText text={it.body} /></p>
                      )}
                    </EditDeleteNote>
                  ) : (
                    it.body && (
                      <p className="mt-1.5 whitespace-pre-wrap rounded-md bg-cream-50 px-3 py-2 text-sm text-ink"><NoteText text={it.body} /></p>
                    )
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {feed.data?.hasMore && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => setLimit((n) => Math.min(n + PAGE, 200))}
            disabled={feed.isFetching}
            className="text-sm font-semibold text-ink hover:text-muted disabled:opacity-50"
          >
            {feed.isFetching ? "Loading…" : "Show more"}
          </button>
        </div>
      )}
    </div>
  );
}
